import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/money";
import { stayPaymentGap } from "@/lib/stayPaymentGap";
import { StayPaymentBreakdown } from "@/components/bookings/StayPaymentBreakdown";
import { canWriteInvoice } from "@/lib/permissions";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { InvoiceDocumentActions } from "@/components/invoices/InvoiceDocumentActions";
import { InvoicePaymentsSection } from "@/components/invoices/InvoicePaymentsSection";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { Booking, BookingDeparture, Invoice, InvoiceListResponse } from "@/types/camping";
import type { InvoiceType } from "@/lib/invoiceType";

export interface GuestStayInvoicesSectionProps {
  booking: Booking | BookingDeparture;
  userGroups: string[];
  active?: boolean;
  onInvoiceDraftOpened: (invoiceId: string) => void;
  onChanged?: () => void;
}

export function GuestStayInvoicesSection({
  booking,
  userGroups,
  active = true,
  onInvoiceDraftOpened,
  onChanged,
}: GuestStayInvoicesSectionProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [invoicesReady, setInvoicesReady] = useState(false);
  const [action, setAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canCreateInvoices =
    booking.status === "checked_in" || booking.status === "checked_out";

  const load = useCallback(() => {
    if (!booking.id || !active) {
      setInvoices([]);
      setInvoicesReady(false);
      return;
    }
    setLoading(true);
    setInvoicesReady(false);
    setError(null);
    api
      .get<InvoiceListResponse>(`/api/v1/invoices?booking_id=${booking.id}`)
      .then((res) => {
        setInvoices(res.items);
        setInvoicesReady(true);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [booking.id, active]);

  useEffect(() => {
    load();
  }, [load]);

  const activeInvoices = useMemo(
    () => invoices.filter((inv) => inv.status !== "cancelled"),
    [invoices],
  );

  const paymentGap = invoicesReady ? stayPaymentGap(booking, invoices) : null;
  const depositInvoices = activeInvoices.filter((inv) => inv.invoice_type === "deposit");
  const hasDeposit = depositInvoices.length > 0;
  const requestedDeposit = Number(booking.deposit_request_amount ?? 0);
  const paidDeposit = Number(booking.deposit_paid_amount ?? 0);
  const hasPaymentRequest = requestedDeposit > 0 || paidDeposit > 0;
  const hasFinal = activeInvoices.some((inv) => inv.invoice_type === "final");
  const hasFull = activeInvoices.some((inv) => inv.invoice_type === "full");
  const issuedDeposit = depositInvoices.some((inv) => isInvoiceFinalized(inv.status));
  const hasDraftOfType = (type: InvoiceType) =>
    invoices.some((inv) => inv.status === "draft" && inv.invoice_type === type);

  const depositSettled = Boolean(booking.deposit_settled_at);
  const canCreateDeposit =
    canInvoice &&
    canCreateInvoices &&
    !depositSettled &&
    !hasDeposit &&
    !hasPaymentRequest &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("deposit");
  const canCreateFull =
    canInvoice &&
    canCreateInvoices &&
    !depositSettled &&
    !hasDeposit &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("full");
  const canCreateFinal =
    canInvoice &&
    canCreateInvoices &&
    !depositSettled &&
    issuedDeposit &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("final");
  const canCreateAny = canCreateDeposit || canCreateFull || canCreateFinal;

  async function createDraft(invoiceType: InvoiceType) {
    setAction(`create-${invoiceType}`);
    setError(null);
    try {
      const created = await api.post<Invoice>("/api/v1/invoices/from-booking", {
        booking_id: booking.id,
        invoice_type: invoiceType,
      });
      load();
      onChanged?.();
      onInvoiceDraftOpened(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnungsentwurf konnte nicht erstellt werden.");
    } finally {
      setAction(null);
    }
  }

  async function issueInvoice(invoiceId: string) {
    setAction(`issue-${invoiceId}`);
    setError(null);
    try {
      await api.post<Invoice>(`/api/v1/invoices/${invoiceId}/issue`, {});
      load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnung konnte nicht ausgestellt werden.");
    } finally {
      setAction(null);
    }
  }

  if (!canInvoice && activeInvoices.length === 0 && !loading && !hasPaymentRequest) return null;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
            Rechnungen & Anzahlungen
          </h3>
          {booking.deposit_settlement_summary ? (
            <p className="mt-0.5 text-xs text-label-secondary">{booking.deposit_settlement_summary}</p>
          ) : hasDeposit ? (
            <p className="mt-0.5 text-xs text-label-secondary">
              {depositInvoices.length} Anzahlung{depositInvoices.length > 1 ? "en" : ""} zu dieser
              Buchung
            </p>
          ) : hasPaymentRequest ? (
            <p className="mt-0.5 text-xs text-label-secondary">
              {paidDeposit > 0
                ? `Zahlungsaufforderung: ${formatCurrency(paidDeposit)} bereits gezahlt. Die Rechnung zur Anreise verrechnet den Betrag.`
                : `Zahlungsaufforderung über ${formatCurrency(requestedDeposit)} ist noch offen.`}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-label-secondary">
              Noch keine Anzahlung hinterlegt
            </p>
          )}
        </div>
        {canCreateAny ? (
          <div className="flex flex-wrap gap-2">
            {canCreateDeposit ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "create-deposit"}
                disabled={!!action}
                onClick={() => void createDraft("deposit")}
              >
                Anzahlung erstellen
              </Button>
            ) : null}
            {canCreateFinal ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "create-final"}
                disabled={!!action}
                onClick={() => void createDraft("final")}
              >
                Schlussrechnung erstellen
              </Button>
            ) : null}
            {canCreateFull ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "create-full"}
                disabled={!!action}
                onClick={() => void createDraft("full")}
              >
                Vollrechnung erstellen
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {paymentGap ? <StayPaymentBreakdown gap={paymentGap} /> : null}

      {!canCreateInvoices && canInvoice && activeInvoices.length === 0 ? (
        <p className="text-sm text-label-secondary">
          Rechnungen und Anzahlungen können nach dem Check-in angelegt werden.
        </p>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Lade Rechnungen…</p>
      ) : activeInvoices.length === 0 ? (
        !canCreateInvoices ? null : (
          <p className="text-sm text-label-secondary">Keine Rechnungen zu dieser Buchung.</p>
        )
      ) : (
        <ul className="space-y-3">
          {invoices.map((inv) => {
            if (inv.status === "cancelled") return null;
            const paidAmount = Number(inv.paid_amount ?? 0);
            const showPayments =
              canInvoice && isInvoiceFinalized(inv.status) && Number(inv.open_amount ?? 0) > 0;

            return (
              <li
                key={inv.id}
                className="rounded-xl border border-separator bg-surface px-3 py-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs text-label-secondary">{inv.invoice_number}</span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <InvoiceTypeBadge invoiceType={inv.invoice_type} />
                    <StatusBadge status={inv.status} kind="invoice" />
                  </div>
                </div>
                <p className="mt-1 tabular-nums text-label">
                  {formatCurrency(inv.total_amount, inv.currency)}
                  {inv.open_amount != null && isInvoiceFinalized(inv.status) ? (
                    <span className="ml-2 text-label-secondary">
                      (offen: {formatCurrency(inv.open_amount, inv.currency)})
                    </span>
                  ) : null}
                  {paidAmount > 0 ? (
                    <span className="ml-2 text-status-green">
                      (bezahlt: {formatCurrency(String(paidAmount), inv.currency)})
                    </span>
                  ) : null}
                </p>
                <InvoiceDocumentActions
                  invoiceId={inv.id}
                  invoiceNumber={inv.invoice_number}
                  status={inv.status}
                  disabled={!!action}
                />
                {canInvoice && inv.status === "draft" ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      loading={action === `issue-${inv.id}`}
                      disabled={!!action}
                      onClick={() => void issueInvoice(inv.id)}
                    >
                      Rechnung ausstellen
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!!action}
                      onClick={() => onInvoiceDraftOpened(inv.id)}
                    >
                      Entwurf bearbeiten
                    </Button>
                  </div>
                ) : null}
                {showPayments ? (
                  <div className="mt-3 border-t border-separator pt-3">
                    <InvoicePaymentsSection
                      invoice={inv}
                      canRecord={canInvoice}
                      onPaymentRecorded={() => {
                        load();
                        onChanged?.();
                      }}
                    />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
